import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline"
import { FC, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useParams } from "react-router-dom"
import { ITestConfig } from "../../../api/models/TestConfig"
import { useApp } from "../../AppProvider"
import { usePutTestConfig } from "../../hooks/useTestConfigs"

interface TestConfigFormProps {
  submitCb?: () => void
  config: ITestConfig
}

export const TestConfigForm: FC<TestConfigFormProps> = ({ config, submitCb }) => {
  const { token, addToast, user } = useApp()
  const { organizationId } = useParams()

  // Track whether sub-sections of the test config are collapsed
  const [isCollapsedState, setIsCollapsedState] = useState(
    config.conditions.reduce<{ [key: string]: boolean }>((state, { condition }) => {
      state[condition] = false
      return state
    }, {}),
  )

  // Form state and mutations
  const {
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<ITestConfig>({
    defaultValues: config,
  })

  const mutation = usePutTestConfig(token, organizationId)

  const onSubmit: SubmitHandler<ITestConfig> = async (formData) => {
    try {
      if (!organizationId && !user?.superAdmin) {
        addToast("Application error", "Unable to verify organization info.")
        return
      }
      let res = await mutation.mutateAsync({
        ...formData,
        _id: config._id,
      })

      if (res.error || res.errors || !res) {
        throw new Error(res.error ?? res.errors?.[0]?.message ?? "Unknown error")
      } else {
        reset()
        submitCb?.()
      }
    } catch (error) {
      addToast("Server error", (error as Error).message, "error")
    }
  }

  return (
    <div className="w-screen max-w-4xl">
      <h2 className="text-xl font-semibold">Edit {config.testName} settings</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-2">
        <fieldset className="space-y-6 md:grid md:grid-cols-2 md:gap-6 md:space-y-0">
          {config.conditions.map((condition, i) => {
            const isCollapsed = isCollapsedState[condition.condition]
            return (
              <div key={i}>
                <button
                  type="button"
                  onClick={() =>
                    setIsCollapsedState({ ...isCollapsedState, [condition.condition]: !isCollapsed })
                  }
                  className="group flex w-full items-center justify-between">
                  <h3 className="font-semibold">{condition.condition}</h3>
                  <div className="text-gray-700 group-hover:text-gray-800 group-active:text-gray-900">
                    {isCollapsed ? <PlusIcon className="h-5 w-5" /> : <MinusIcon className="h-5 w-5" />}
                  </div>
                </button>
                {!isCollapsed ? (
                  <div className="space-y-1">
                    <div className="label mt-2 -mb-2">Thresholds</div>
                    {Object.entries(condition.thresholds).map(([threshold, value], j) => (
                      <div key={j}>
                        <label className="flex w-full items-center justify-between">
                          {threshold}
                          <input
                            {...register(`conditions.${i}.thresholds.${threshold}`, { required: true })}
                            type="number"
                            step={0.01}
                            className="input w-fit"
                          />
                        </label>
                      </div>
                    ))}
                    <label className="label">
                      Recommendation message
                      <textarea
                        {...register(`conditions.${i}.message`, { required: true })}
                        className="input h-auto"
                        rows={2}></textarea>
                    </label>
                  </div>
                ) : null}
              </div>
            )
          })}
          <div className="mt-6 flex justify-end gap-x-2 md:col-span-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                reset()
                submitCb?.()
              }}
              disabled={mutation.isPending}>
              Cancel
            </button>

            <button type="submit" className="btn" disabled={!isDirty || mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  )
}
